import { fold } from '../text/fold.js';

/**
 * The ONE unit vocabulary of the logistics feature, and the identity rule
 * every unit comparison goes through. Server-safe (no i18n): the backend may
 * import it to compare units the way the clients do.
 *
 * A unit is stored as a stable KEY ('sheets', 'tons', 'm3'). It used to be
 * stored as the author's rendered label ('sheets' in English, '枚' in
 * Japanese), which froze it in the author's language and split one unit into
 * two: stock tracked in 'sheets' and an order typed as '枚' read as different
 * units to the mismatch warning, the arrival merge and the prefill.
 *
 * Order is rough frequency on site — the pickers list it as is.
 */
export const UNIT_KEYS = [
  'm3',
  'm2',
  'm',
  'kg',
  'tons',
  'liters',
  'pieces',
  'rods',
  'sheets',
  'bags',
  'rolls',
  'bundles',
  'boxes',
  'cans',
  'drums',
  'pallets',
  'sets',
  'units',
] as const;

export type UnitKey = (typeof UNIT_KEYS)[number];

/**
 * Per-key facts. `legacyLabels`: every label a client has ever RENDERED (and so
 * stored) for the key — the web and mobile `logistics.stockDialog.units.*`
 * strings, English and Japanese (both apps carry identical lists; their
 * history holds no other variant). Frozen on purpose: this is a record of what
 * is already in the database, not the live copy. A renamed label in a locale
 * file must be ADDED here, never swapped, or rows saved under the old label
 * stop resolving. `counted`: the unit counts whole things (bags, sheets) rather
 * than measuring a quantity (t, m3, kg).
 */
const UNIT_CATALOG: Record<UnitKey, { legacyLabels: readonly string[]; counted: boolean }> = {
  m3: { legacyLabels: ['Cu. m.', '㎥'], counted: false },
  m2: { legacyLabels: ['Sq. m.', '㎡'], counted: false },
  m: { legacyLabels: ['m'], counted: false },
  kg: { legacyLabels: ['kg'], counted: false },
  tons: { legacyLabels: ['t'], counted: false },
  liters: { legacyLabels: ['L'], counted: false },
  pieces: { legacyLabels: ['pcs', '個'], counted: true },
  rods: { legacyLabels: ['rods', '本'], counted: true },
  sheets: { legacyLabels: ['sheets', '枚'], counted: true },
  bags: { legacyLabels: ['bags', '袋'], counted: true },
  rolls: { legacyLabels: ['rolls', '巻'], counted: true },
  bundles: { legacyLabels: ['bundles', '束'], counted: true },
  boxes: { legacyLabels: ['boxes', '箱'], counted: true },
  cans: { legacyLabels: ['cans', '缶'], counted: true },
  drums: { legacyLabels: ['drums', 'ドラム缶'], counted: true },
  pallets: { legacyLabels: ['pallets', 'パレット'], counted: true },
  sets: { legacyLabels: ['set', '式'], counted: true },
  units: { legacyLabels: ['units', '台'], counted: true },
};

/** Folded key-or-label → key. NFKC alone folds '㎥' onto 'm3', so the key and
 *  its Japanese label land on one entry either way. */
const KEY_BY_FOLDED: ReadonlyMap<string, UnitKey> = new Map(
  UNIT_KEYS.flatMap((key) => [key, ...UNIT_CATALOG[key].legacyLabels].map((text) => [fold(text), key] as const)),
);

/**
 * The key a stored unit names — a key itself, or a legacy label in either
 * language — or null for free text the vocabulary does not know (a unit typed
 * on the scheduler, or anything older than the picker).
 */
export function unitKeyOf(stored: string | null | undefined): UnitKey | null {
  if (stored == null) return null;
  return KEY_BY_FOLDED.get(fold(stored.trim())) ?? null;
}

/**
 * What two units are compared BY: the key when the vocabulary knows the unit,
 * else the folded text. 'sheets', 'Sheets' and '枚' are one unit; two pieces
 * of free text are the same unit when they fold equal, as before.
 */
export function unitIdentity(stored: string | null | undefined): string {
  return unitKeyOf(stored) ?? fold((stored ?? '').trim());
}

/** Whether two stored units are the same unit, in whatever language either
 *  was written. Two blanks are equal (both "no unit recorded"). */
export function sameUnit(a: string | null | undefined, b: string | null | undefined): boolean {
  return unitIdentity(a) === unitIdentity(b);
}

/** What a write stores for a unit: its key when the vocabulary knows it (a
 *  label picked in either language included), else the text as typed. */
export function unitToStore(text: string): string {
  return unitKeyOf(text) ?? text.trim();
}

/** Whether a stored unit counts whole things (bags, sheets) — in either
 *  language. A measured unit (t, m3) and free text the vocabulary does not
 *  know are not. */
export function isCountedUnit(stored: string | null | undefined): boolean {
  const key = unitKeyOf(stored);
  return key !== null && UNIT_CATALOG[key].counted;
}

/** The unit a material is counted in on site (folded name match), or null
 *  when the site does not track it — what a delivery line for that material
 *  must use (server `assertStockUnits`, client `draftsFromInitial`). */
export type StockUnitOf = (itemName: string) => string | null;
